'use client';

import Icon from '@/components/ui/AppIcon';

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: string;
  color: string;
  change?: string;
}

function MetricCard({ title, value, icon, color, change }: MetricCardProps) {
  return (
    <div className="bg-card rounded-xl p-6 shadow-card border border-border">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 rounded-lg bg-${color}/10 flex items-center justify-center`}>
          <Icon name={icon} size={24} className={`text-${color}`} />
        </div>
        {change && (
          <span className="text-sm font-medium text-success flex items-center gap-1">
            <Icon name="ArrowTrendingUpIcon" size={16} />
            {change}
          </span>
        )}
      </div>
      <h3 className="text-sm font-medium text-muted-foreground mb-1">{title}</h3>
      <p className="text-3xl font-bold text-foreground">{value}</p>
    </div>
  );
}

export default function AdminMetrics() {
  return (
    <div className="space-y-8">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Shipments"
          value="1,247"
          icon="public/assets/images/ChatGPT_Image_Feb_7_2026_02_48_47_AM-1770428942096.png"
          color="primary"
          change="+12%"
        />
        <MetricCard
          title="Active Orders"
          value="342"
          icon="ClockIcon"
          color="secondary"
          change="+8%"
        />
        <MetricCard
          title="Total Customers"
          value="856"
          icon="UsersIcon"
          color="accent"
          change="+15%"
        />
        <MetricCard
          title="Pending Assignments"
          value="28"
          icon="ExclamationTriangleIcon"
          color="destructive"
        />
      </div>

      {/* Status Overview */}
      <div className="bg-card rounded-xl p-6 shadow-card border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-6">Shipment Status Overview</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-primary/5 rounded-lg">
            <p className="text-2xl font-bold text-primary mb-1">156</p>
            <p className="text-sm text-muted-foreground">In Transit</p>
          </div>
          <div className="text-center p-4 bg-secondary/5 rounded-lg">
            <p className="text-2xl font-bold text-secondary mb-1">89</p>
            <p className="text-sm text-muted-foreground">Out for Delivery</p>
          </div>
          <div className="text-center p-4 bg-success/5 rounded-lg">
            <p className="text-2xl font-bold text-success mb-1">742</p>
            <p className="text-sm text-muted-foreground">Delivered</p>
          </div>
          <div className="text-center p-4 bg-accent/5 rounded-lg">
            <p className="text-2xl font-bold text-accent mb-1">97</p>
            <p className="text-sm text-muted-foreground">Pending Pickup</p>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-card rounded-xl p-6 shadow-card border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-6">Recent Activity</h3>
        <div className="space-y-4">
          {[
            { action: 'Order TRK-2026-001234 assigned to Driver John Smith', time: '5 minutes ago', icon: 'UserPlusIcon' },
            { action: 'Shipment TRK-2026-005678 status updated to Out for Delivery', time: '12 minutes ago', icon: 'public/assets/images/ChatGPT_Image_Feb_7_2026_02_48_47_AM-1770428942096.png' },
            { action: 'Bulk status update completed for 15 shipments', time: '1 hour ago', icon: 'CheckCircleIcon' },
            { action: 'New customer Sarah Johnson registered', time: '2 hours ago', icon: 'UserIcon' },
          ].map((activity, index) => (
            <div key={index} className="flex items-start gap-3 pb-4 border-b border-border last:border-0">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name={activity.icon} size={16} className="text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-foreground">{activity.action}</p>
                <p className="text-xs text-muted-foreground mt-1">{activity.time}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}